/*
 * STEP 4 — Voice Tributes & Background Music
 * Design: Quiet Reverence
 * Browser-based audio recorder + file upload.
 * Waveform visualizer using CSS animation bars.
 * Supports MP3, WAV, M4A (phone native).
 *
 * v2: Added InlineMusicPicker — fetches from Worker REST API
 *     GET /api/director/music  (list by mood)
 *     GET /api/director/music/:id/presign  (playback URL)
 *     No tRPC dependency.
 */

import { ACCEPTED_AUDIO_TYPES, AudioAsset, formatFileSize } from "./intake";
import { Mic, Music, Upload, X, Square, Play, Pause, Library, Loader2, Check } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
const nanoid = (n = 10) => Math.random().toString(36).substring(2, 2 + n);
import { toast } from "sonner";

interface Props {
  audio: AudioAsset[];
  onChange: (audio: AudioAsset[]) => void;
  /** R2 key of the currently selected library track (from state.deceased.musicTrack) */
  selectedMusicKey?: string;
  selectedMusicTitle?: string;
  onMusicSelect?: (track: { title: string; r2Key: string }) => void;
}

const MAX_AUDIO = 6;
const WAVEFORM_BARS = 20;

// ── Mood labels for filter tabs ───────────────────────────────────────────────
const MOOD_LABELS: Record<string, string> = {
  somber:      'Somber',
  reflective:  'Reflective',
  peaceful:    'Peaceful',
  uplifting:   'Uplifting',
  celebratory: 'Celebratory',
  upbeat:      'Upbeat',
  nostalgic:   'Nostalgic',
  spiritual:   'Spiritual',
  custom:      'Custom',
};

type LibraryTrack = {
  id: number;
  r2Key: string;
  filename: string;
  title: string | null;
  artist: string | null;
  mood: string;
  durationSec: number | null;
};

function fmtDur(s: number | null): string {
  if (!s) return '';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── Mini inline audio preview player ─────────────────────────────────────────
function TrackPreview({ trackId }: { trackId: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = async () => {
    if (!url) {
      setLoading(true);
      try {
        const res = await fetch(`/api/director/music/${trackId}/presign`);
        const data = await res.json() as { url: string };
        setUrl(data.url);
        setPlaying(true);
      } catch {
        toast.error('Could not load preview.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (audioRef.current) {
      if (audioRef.current.paused) { audioRef.current.play(); setPlaying(true); }
      else { audioRef.current.pause(); setPlaying(false); }
    }
  };

  return (
    <div className="flex items-center">
      {url && (
        <audio
          ref={audioRef}
          src={url}
          autoPlay
          onEnded={() => setPlaying(false)}
          className="hidden"
        />
      )}
      <button
        type="button"
        onClick={toggle}
        disabled={loading}
        className="w-7 h-7 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition disabled:opacity-40"
        title={playing ? 'Pause' : 'Preview'}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin text-primary" />
        ) : playing ? (
          <Pause className="w-3 h-3 text-primary" />
        ) : (
          <Play className="w-3 h-3 text-primary" />
        )}
      </button>
    </div>
  );
}

// ── Inline Music Library Picker ───────────────────────────────────────────────
interface MusicPickerProps {
  selectedKey?: string;
  onSelect: (track: { title: string; r2Key: string }) => void;
  onClose: () => void;
}
function InlineMusicPicker({ selectedKey, onSelect, onClose }: MusicPickerProps) {
  const [tracks, setTracks] = useState<LibraryTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = mood !== 'all' ? `?mood=${mood}` : '';
    fetch(`/api/director/music${params}`)
      .then(r => r.json())
      .then((data: { tracks: LibraryTrack[] }) => setTracks(data.tracks ?? []))
      .catch(() => toast.error('Could not load music library.'))
      .finally(() => setLoading(false));
  }, [mood]);

  const filtered = search
    ? tracks.filter(t =>
        (t.title ?? t.filename).toLowerCase().includes(search.toLowerCase()) ||
        (t.artist ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : tracks;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Library className="w-4 h-4 text-primary" />
          <span className="font-['Playfair_Display'] font-600 text-foreground text-sm">Music Library</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-6 h-6 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-2">
        <input
          type="text"
          placeholder="Search by title or artist…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
        />
      </div>

      {/* Mood tabs */}
      <div className="flex gap-1.5 overflow-x-auto px-4 pb-2 scrollbar-hide">
        {['all', ...Object.keys(MOOD_LABELS)].map(m => (
          <button
            key={m}
            type="button"
            onClick={() => { setMood(m); setSearch(''); }}
            className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-all ${
              mood === m
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
            }`}
          >
            {m === 'all' ? 'All' : MOOD_LABELS[m]}
          </button>
        ))}
      </div>

      {/* Track list */}
      <div className="max-h-72 overflow-y-auto px-4 pb-3 space-y-1.5">
        {loading && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading library…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">
            <Music className="w-7 h-7 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tracks found.</p>
            <p className="text-xs mt-1">Upload MP3s to the <code>sovereign-music</code> R2 bucket and scan the library.</p>
          </div>
        )}
        {filtered.map(track => {
          const label = track.title ?? track.filename;
          const isSelected = track.r2Key === selectedKey;
          return (
            <div
              key={track.id}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-colors ${
                isSelected
                  ? 'bg-primary/5 border-primary/30'
                  : 'bg-background border-border hover:border-primary/30'
              }`}
            >
              <TrackPreview trackId={track.id} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{label}</p>
                {track.artist && <p className="text-xs text-muted-foreground truncate">{track.artist}</p>}
              </div>
              {track.durationSec && (
                <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{fmtDur(track.durationSec)}</span>
              )}
              <button
                type="button"
                onClick={() => { onSelect({ title: label, r2Key: track.r2Key }); onClose(); }}
                className={`shrink-0 flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition ${
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted text-muted-foreground border-border hover:bg-primary/10 hover:border-primary/40'
                }`}
              >
                {isSelected && <Check className="w-3 h-3" />}
                {isSelected ? 'Selected' : 'Select'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Step4Audio Component ─────────────────────────────────────────────────
export default function Step4Audio({
  audio,
  onChange,
  selectedMusicKey,
  selectedMusicTitle,
  onMusicSelect,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  const [waveHeights] = useState(() =>
    Array.from({ length: WAVEFORM_BARS }, () => Math.random() * 0.7 + 0.3)
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-tribute-${Date.now()}.webm`, { type: 'audio/webm' });
        const newAsset: AudioAsset = {
          id: nanoid(),
          file,
          name: `Voice Tribute ${audio.filter(a => a.type === 'voicemail').length + 1}`,
          durationLabel: formatSecs(recordingSeconds),
          type: 'voicemail',
        };
        onChange([...audio, newAsset]);
        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        setRecordingSeconds(0);
        setRecording(false);
      };

      mr.start();
      setRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch {
      toast.error('Microphone access is required to record a voice tribute.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const remaining = MAX_AUDIO - audio.length;
    const toAdd = arr.slice(0, remaining);
    const newAssets: AudioAsset[] = toAdd.map(file => ({
      id: nanoid(),
      file,
      name: file.name.replace(/\.[^/.]+$/, ''),
      durationLabel: '',
      type: file.name.toLowerCase().includes('music') || file.name.toLowerCase().includes('song') ? 'music' : 'voicemail',
    }));
    onChange([...audio, ...newAssets]);
  }, [audio, onChange]);

  const remove = (id: string) => onChange(audio.filter(a => a.id !== id));
  const updateName = (id: string, name: string) =>
    onChange(audio.map(a => a.id === id ? { ...a, name } : a));
  const updateType = (id: string, type: 'voicemail' | 'music') =>
    onChange(audio.map(a => a.id === id ? { ...a, type } : a));

  return (
    <div className="step-enter space-y-6">
      <p className="text-sm text-muted-foreground">
        Record a voice tribute, upload your own audio, or choose background music from our library.
        Voice tributes are woven into the video. Background music plays beneath the visuals.
      </p>

      {/* ── Background Music from Library ─────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-primary" />
            <span className="font-['Playfair_Display'] font-600 text-foreground">Background Music</span>
          </div>
          {onMusicSelect && (
            <button
              type="button"
              onClick={() => setShowPicker(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary text-xs font-medium hover:bg-primary/10 transition"
            >
              <Library className="w-3 h-3" />
              {showPicker ? 'Close Library' : 'Browse Library'}
            </button>
          )}
        </div>

        {/* Selected track display */}
        {selectedMusicTitle && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/30 bg-primary/5">
            <Music className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{selectedMusicTitle}</p>
              <p className="text-xs text-muted-foreground">Selected from library</p>
            </div>
            {onMusicSelect && (
              <button
                type="button"
                onClick={() => onMusicSelect({ title: '', r2Key: '' })}
                className="w-5 h-5 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition flex-shrink-0"
                title="Remove selection"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        {!selectedMusicTitle && !showPicker && (
          <p className="text-xs text-muted-foreground italic">No background music selected — click "Browse Library" to choose a track.</p>
        )}

        {/* Inline picker */}
        {showPicker && onMusicSelect && (
          <InlineMusicPicker
            selectedKey={selectedMusicKey}
            onSelect={onMusicSelect}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>

      {/* ── Browser Recorder ──────────────────────────────────────────────── */}
      <div className="p-5 rounded-xl border-2 border-border bg-card space-y-4">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-primary" />
          <span className="font-['Playfair_Display'] font-600 text-foreground">Record a Voice Tribute</span>
        </div>

        {recording ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-1 h-12">
              {waveHeights.map((h, i) => (
                <div
                  key={i}
                  className="waveform-bar"
                  style={{
                    height: `${h * 100}%`,
                    animationDelay: `${i * 0.04}s`,
                    animationDuration: `${0.6 + Math.random() * 0.4}s`,
                  }}
                />
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-primary tabular-nums">{formatSecs(recordingSeconds)}</span>
              <button
                type="button"
                onClick={stopRecording}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-destructive text-destructive-foreground font-medium text-sm hover:opacity-90 transition"
              >
                <Square className="w-4 h-4" />
                Stop Recording
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={audio.length >= MAX_AUDIO}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition disabled:opacity-40"
          >
            <Mic className="w-4 h-4" />
            Start Recording
          </button>
        )}
      </div>

      {/* ── File Upload ───────────────────────────────────────────────────── */}
      {audio.length < MAX_AUDIO && (
        <div
          className="upload-frame p-6 flex flex-col items-center gap-3 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Music className="w-5 h-5 text-primary" />
            </div>
          </div>
          <div className="text-center">
            <p className="font-['Playfair_Display'] font-600 text-foreground">Upload Your Own Audio</p>
            <p className="text-xs text-muted-foreground mt-1">Voicemails, tributes, or your own music file</p>
            <p className="text-xs text-muted-foreground">MP3, WAV, M4A, AAC</p>
          </div>
          <input
            ref={fileInputRef}
            id="audio-file-input"
            name="audio_files"
            type="file"
            accept={ACCEPTED_AUDIO_TYPES}
            multiple
            className="hidden"
            onChange={e => e.target.files && addFiles(e.target.files)}
          />
        </div>
      )}

      {/* ── Audio List ────────────────────────────────────────────────────── */}
      {audio.length > 0 && (
        <div className="space-y-3">
          {audio.map(asset => (
            <div key={asset.id} className="thumb-enter flex gap-3 p-3 bg-card rounded-xl border border-border items-start">
              <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                asset.type === 'music' ? 'bg-secondary' : 'bg-primary/10'
              }`}>
                {asset.type === 'music' ? (
                  <Music className="w-5 h-5 text-secondary-foreground" />
                ) : (
                  <Mic className="w-5 h-5 text-primary" />
                )}
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <input
                  id={`audio-name-${asset.id}`}
                  name={`audio_name_${asset.id}`}
                  type="text"
                  value={asset.name}
                  onChange={e => updateName(asset.id, e.target.value)}
                  className="w-full px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">{asset.file ? formatFileSize(asset.file.size) : asset.r2Key ? '✓ Uploaded' : ''}</span>
                  {asset.durationLabel && <span className="text-xs text-muted-foreground">· {asset.durationLabel}</span>}
                  <div className="flex gap-1 ml-auto">
                    <button
                      type="button"
                      onClick={() => updateType(asset.id, 'voicemail')}
                      className={`text-xs px-2 py-0.5 rounded-full border transition ${
                        asset.type === 'voicemail'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Mic className="w-3 h-3 inline mr-1" />Tribute
                    </button>
                    <button
                      type="button"
                      onClick={() => updateType(asset.id, 'music')}
                      className={`text-xs px-2 py-0.5 rounded-full border transition ${
                        asset.type === 'music'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Music className="w-3 h-3 inline mr-1" />Music
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(asset.id)}
                className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {audio.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground font-['Playfair_Display'] italic">
          A voice tribute transforms a slideshow into a living memory.
        </div>
      )}
    </div>
  );
}

function formatSecs(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}
